import { Navigate, Outlet } from 'react-router';
import { EXPENSES_GATE } from './access';
import { type ExpensesPermissionsState, useExpensesPermissions } from './use-expenses-permissions';

/**
 * The route half of `EXPENSES_GATE` (the rail reads the same constant from the module catalog). A
 * caller who holds neither `expenses:expense:submit` nor `expenses:expense:read-all` goes to the
 * dashboard; while the rule is still being read nothing renders, so a reload does not bounce
 * someone entitled. Convenience only: the finance service's guards decide. (The nexpense cut-over
 * notice is the shell's, on the two lists once this gate is open: `shell/NoticeArea.tsx`, #956.)
 */
export function ExpensesGate() {
  const permissions = useExpensesPermissions();
  const open = expensesGateOpen(permissions);
  if (open === 'unknown') return null;
  if (!open) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Every gate kind is named, so the compiler points here whenever `EXPENSES_GATE` changes kind. The
 * ones «Note spese» does not use close the route.
 */
export function expensesGateOpen(permissions: ExpensesPermissionsState): boolean | 'unknown' {
  const gate = EXPENSES_GATE;
  switch (gate.kind) {
    case 'permission':
      if (permissions.status === 'loading') return 'unknown';
      // Fails closed: an error holds no permission.
      return (
        permissions.status === 'ready' &&
        gate.anyOf.some((code) => permissions.permissions.includes(code))
      );
    case 'always':
      return true;
    case 'workspace-domain':
    case 'finance-capability':
    case 'operator':
    case 'administrator':
    case 'registry':
      return false;
    default: {
      const unhandled: never = gate;
      return unhandled;
    }
  }
}
